import type { SyncConflict, SyncEntityType, SyncMutationDraft } from './types';

export type SyncEntityReference = Pick<SyncMutationDraft, 'entityType' | 'entityId' | 'payload'>;

export const syncEntityKey = ({ entityType, entityId }: Pick<SyncEntityReference, 'entityType' | 'entityId'>): string =>
  `${entityType}:${entityId.toLowerCase()}`;

export const syncPayloadBookId = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;
  const value = (payload as { bookId?: unknown }).bookId;
  return typeof value === 'string' ? value.toLowerCase() : undefined;
};

export const syncEntityBookId = (entityType: SyncEntityType, entityId: string, payload: unknown): string | undefined =>
  entityType === 'book' ? entityId.toLowerCase() : syncPayloadBookId(payload);

export const areSyncEntitiesRelated = (
  left: SyncEntityReference,
  right: SyncEntityReference
): boolean => {
  if (syncEntityKey(left) === syncEntityKey(right)) return true;
  const leftBookId = syncEntityBookId(left.entityType, left.entityId, left.payload);
  const rightBookId = syncEntityBookId(right.entityType, right.entityId, right.payload);
  return Boolean(leftBookId && rightBookId && leftBookId === rightBookId);
};

export const conflictBookIds = (conflict: SyncConflict): Set<string> => {
  const ids = new Set<string>();
  const ownBookId = syncEntityBookId(
    conflict.entityType,
    conflict.entityId,
    conflict.localPayload ?? conflict.remotePayload
  );
  if (ownBookId) ids.add(ownBookId);
  const remoteBookId = syncPayloadBookId(conflict.remotePayload);
  if (remoteBookId) ids.add(remoteBookId);
  if (conflict.dependencyEntityType === 'book' && conflict.dependencyEntityId) {
    ids.add(conflict.dependencyEntityId.toLowerCase());
  }
  if (conflict.dependentEntityType === 'book' && conflict.dependentEntityId) {
    ids.add(conflict.dependentEntityId.toLowerCase());
  }
  return ids;
};

export const isSyncEntityRelatedToConflict = (
  entity: SyncEntityReference,
  conflict: SyncConflict
): boolean => {
  if (areSyncEntitiesRelated(entity, {
    entityType: conflict.entityType,
    entityId: conflict.entityId,
    payload: conflict.localPayload ?? conflict.remotePayload
  } as SyncEntityReference)) return true;
  const bookId = syncEntityBookId(entity.entityType, entity.entityId, entity.payload);
  return Boolean(bookId && conflictBookIds(conflict).has(bookId));
};

export const hasRelatedSyncConflict = (
  drafts: readonly SyncMutationDraft[],
  conflicts: readonly SyncConflict[]
): boolean => conflicts.some((conflict) =>
  drafts.some((draft) => isSyncEntityRelatedToConflict(draft, conflict)));
